import React, { useEffect, useState, useMemo, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { withRouter } from "react-router-dom";
import {
  Radio,
  Input,
  INPUT_SIZES,
  Button,
  BUTTON_VARIANTS,
  IconSearch,
  INPUT_ICON_POSITIONS,
  ListItem,
  ListItemPrimaryText,
  ListItemText,
  ListWrapper,
  Popup,
  Loader,
} from "cdk-radial";
import "./appOrgSecretsFetch.scss";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import WithNavigationToHome from "../components/WithNavigationToHome";
import axios from "axios";
import SecretsTable from "./SecretsTable";
import config from "../config/app.conf.json";
const SEARCH_TYPE = { APP: "APP", ORG: "ORG" };

function AppOrgSecretsFetch({ auth }) {
  const [searchOption, setSearchOption] = useState(SEARCH_TYPE.APP);
  const [searchInput, setSearchInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [fetchingSearchResults, setFetchingSearchResults] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [selected, setSelected] = useState(null);
  const [fetchingSecrets, setFetchingSecrets] = useState(false);
  const [secrets, setSecrets] = useState(null);

  const searchInputIsValid = useMemo(() => !!searchInput, [searchInput]);

  useEffect(() => {
    setIsOpen(true);
  }, [searchResults]);

  const onRadioChange = (event) => {
    setSearchOption(event.target.value);
    setSearchInput("");
    setSearchResults([]);
    setNoResultsFound(false);
    setSecrets(null);
    setSelected(null);
  };

  const handleInputChange = (event) => {
    setSearchInput(event.target.value);
    setNoResultsFound(false);
    setSecrets(null);
    setSelected(null);
  };

  const handleSearch = async () => {
    setFetchingSearchResults(true);
    setNoResultsFound(false);
    setSearchResults([]);
    setSecrets(null);
    setSelected(null);
    const token = auth?.accessToken;
    let url = `${config.api.marketplace_service_url}/graphql`;
    let body, key;
    if (searchOption === SEARCH_TYPE.APP) {
      body = {
        variables: {
          searchText: searchInput,
          searchOnly: true,
          needAllSolutionVisibilityTypes: true,
          isAdmin: true,
        },
        query: `query ($from: Int, $sortBy: String, $order: String, $searchText: String, $searchOnly: Boolean, $needAllSolutionVisibilityTypes: Boolean, $isAdmin: Boolean) {\n  searchStoreFrontSolution(filters: {from: $from, sortBy: $sortBy, order: $order, searchText: $searchText, searchOnly: $searchOnly, needAllSolutionVisibilityTypes: $needAllSolutionVisibilityTypes, isAdmin: $isAdmin}) {\n    data { id name displayName }\n  }\n}`,
      };
      key = "searchStoreFrontSolution";
    } else {
      body = {
        variables: { orgName: searchInput },
        query: `query ($orgName: String) {\n  getOrganizations(orgName: $orgName) { id name }\n}`,
      };
      key = "getOrganizations";
    }
    try {
      const res = await axios.post(url, body, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      let results =
        searchOption === SEARCH_TYPE.APP
          ? res.data.data[key].data
          : res.data.data[key];
      if (!results || results.length === 0) {
        setNoResultsFound(true);
      } else {
        setSearchResults(results);
      }
    } catch (e) {
      setNoResultsFound(true);
    } finally {
      setFetchingSearchResults(false);
    }
  };

  const fetchSecrets = async (id, nameOrDisplayName) => {
    setFetchingSecrets(true);
    setSecrets(null);
    setSelected(id);
    if (nameOrDisplayName) setSearchInput(nameOrDisplayName);
    setIsOpen(false);
    const token = auth?.accessToken;
    let url =
      searchOption === SEARCH_TYPE.APP
        ? `${config.api.solutions_svc_base_url}/v1/admin/solutions/credentials?solutionId=${id}`
        : `${config.api.solutions_svc_base_url}/v1/admin/solutions/credentials?organizationId=${id}`;
    try {
      const res = await axios.get(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      let secretsArr = [];
      if (Array.isArray(res.data)) {
        secretsArr = res.data;
      } else if (
        res.data &&
        res.data.secrets &&
        Array.isArray(res.data.secrets)
      ) {
        secretsArr = res.data.secrets;
      }
      const mappedSecrets = secretsArr.map((item) => ({
        appId: item.appId || item.app_id,
        appName: item.appName || item.display_name,
        orgName: item.orgName || item.org_name,
        orgId: item.orgId || item.org_id,
        apiKey: item.apiKey || item.api_key,
        apiSecret: item.apiSecret || item.api_secret,
        status: item.status,
      }));
      setSecrets(mappedSecrets);
    } catch (e) {
      let errorMsg = "Failed to fetch secrets";
      if (
        e.response &&
        e.response.status === 404 &&
        e.response.data &&
        e.response.data.message
      ) {
        errorMsg = e.response.data.message;
      } else if (e.response && e.response.data && e.response.data.message) {
        errorMsg = e.response.data.message;
      }
      setSecrets({ error: errorMsg });
    } finally {
      setFetchingSecrets(false);
    }
  };

  return (
    <div className="component-content">
      <div className="fm-page-content-grid max-width-resp page-padding-common">
        <div className="secrets-data-page-title">
          <FormattedMessage
            id="AppCredentialsLookup.page.title"
            defaultMessage="App Credentials"
          />
        </div>

        <div className="search-secrets">
          <div className="secrets-search-radio-buttons">
            <span>Search By:</span>
            <Radio
              id="app-org-secrets-search-app"
              name="app-org-secrets-search"
              checked={searchOption === SEARCH_TYPE.APP}
              label="App"
              onChange={onRadioChange}
              value={SEARCH_TYPE.APP}
            />
            <Radio
              id="app-org-secrets-search-org"
              name="app-org-secrets-search"
              checked={searchOption === SEARCH_TYPE.ORG}
              label="Organization"
              onChange={onRadioChange}
              value={SEARCH_TYPE.ORG}
            />
          </div>
          <form
            className="secrets-search-bar"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSearch();
            }}
          >
            <div>
              <Input
                id="app-org-secrets-search-input"
                onChange={handleInputChange}
                placeholder={
                  searchOption === SEARCH_TYPE.APP
                    ? "Search for an app by name or enter an app ID"
                    : "Search for an org by name or enter an org ID"
                }
                value={searchInput}
                hideLablse={true}
                size={INPUT_SIZES.SMALL}
                icon={<IconSearch />}
                iconPosition={INPUT_ICON_POSITIONS.LEADING}
                hasClearButton={true}
                onInputClear={() => setSearchInput("")}
                dataTestId="search-input"
              />
              {searchResults.length ? (
                <div className="secrets-search-results-container">
                  <Popup
                    style={{ background: "#fff", zIndex: "5" }}
                    dataTestId="popup"
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    onOpen={() => {}}
                    wrappedComponentContent={<div />}
                  >
                    <ListWrapper
                      style={{ width: "100%" }}
                      className={isOpen ? "search-results-popup" : null}
                    >
                      {searchResults.map((item) => (
                        <ListItem
                          key={item.id}
                          onClick={() =>
                            fetchSecrets(item.id, item.displayName || item.name)
                          }
                        >
                          <ListItemText>
                            <ListItemPrimaryText>
                              {item.displayName || item.name}
                            </ListItemPrimaryText>
                          </ListItemText>
                        </ListItem>
                      ))}
                    </ListWrapper>
                  </Popup>
                </div>
              ) : null}
              {!searchResults.length && noResultsFound && searchInput ? (
                <ListWrapper
                  style={{ width: "100%" }}
                  className={isOpen ? "search-results-popup" : null}
                >
                  <ListItem>
                    <ListItemText>
                      <ListItemPrimaryText>
                        No Results Found
                      </ListItemPrimaryText>
                    </ListItemText>
                  </ListItem>
                </ListWrapper>
              ) : null}
            </div>
            <Button
              text="Search"
              variant={BUTTON_VARIANTS.PRIMARY}
              isDisabled={!searchInputIsValid || fetchingSearchResults}
              isLoading={fetchingSearchResults}
              type="submit"
            />
          </form>
        </div>
        <div>
          {fetchingSecrets ? (
            <div className="fetch-secrets-loader">
              <Loader style={{ stroke: "#5e2368" }} label="Fetching Secrets" />
            </div>
          ) : Array.isArray(secrets) && secrets.length > 0 ? (
            <SecretsTable secrets={secrets} />
          ) : secrets ? (
            <div style={{ marginTop: 24 }}>
              <h4>Secrets</h4>
              <pre
                style={{ background: "#f4f4f4", padding: 12, borderRadius: 4 }}
              >
                {secrets && typeof secrets === "object" && secrets.error
                  ? secrets.error
                  : Array.isArray(secrets)
                  ? JSON.stringify(secrets, null, 2)
                  : typeof secrets === "string"
                  ? secrets
                  : JSON.stringify(secrets, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const WrappedAppOrgSecretsFetch = WithNavigationToHome(AppOrgSecretsFetch);
export default withAuth(withRouter(WrappedAppOrgSecretsFetch));
